import React, { useState } from 'react';
import { Copy, Check, X, Info, Circle } from 'lucide-react';

export function DnsRecordsModal({ domain, onClose }) {
  const [copied, setCopied] = useState(null);

  const records = [
    {
      type: 'MX',
      name: domain.domain_name,
      priority: 10,
      value: 'mail.novamail.io',
      verified: domain.mx_verified,
    },
    {
      type: 'SPF',
      name: domain.domain_name,
      value: 'v=spf1 include:sendgrid.net include:ses.amazonaws.com include:novamail.io ~all',
      verified: domain.spf_verified,
    },
    {
      type: 'DKIM',
      name: `novamail._domainkey.${domain.domain_name}`,
      value: domain.dkim_public_key ? `v=DKIM1; k=rsa; p=${domain.dkim_public_key}` : 'Generating...',
      verified: domain.dkim_verified,
    },
    {
      type: 'DMARC',
      name: `_dmarc.${domain.domain_name}`,
      value: 'v=DMARC1; p=none; rua=mailto:dmarc@novamail.io',
      verified: domain.dmarc_verified,
    },
  ];

  function copyToClipboard(text, id) {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900">DNS Records - {domain.domain_name}</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
            <p className="font-bold mb-2 flex items-center gap-2">
              <Info size={16} /> Setup Instructions
            </p>
            <ol className="list-decimal list-inside space-y-1.5 ml-1 text-blue-700">
              <li>Log in to your domain registrar (GoDaddy, Namecheap, etc.)</li>
              <li>Go to DNS management for <strong>{domain.domain_name}</strong></li>
              <li>Add/update each record below with the values shown</li>
              <li>Click "Verify DNS" on the dashboard when done</li>
              <li>Note: DNS changes can take up to 24 hours to propagate</li>
            </ol>
          </div>

          {records.map((record, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md font-mono text-sm font-bold border border-slate-200">
                    {record.type}
                  </span>
                  <h3 className="font-semibold text-slate-900">Record</h3>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full border flex items-center gap-1 ${record.verified
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>
                  {record.verified ? <Check size={12} /> : <Circle size={12} />}
                  {record.verified ? 'Verified' : 'Pending'}
                </span>
              </div>

              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-[80px_1fr] gap-4 items-center">
                  <span className="text-slate-500 font-medium">Name</span>
                  <div className="flex gap-2 items-center">
                    <div className="font-mono text-slate-700 bg-slate-50 px-3 py-1.5 rounded border border-slate-200 break-all">
                      {record.name}
                    </div>
                    <button
                      onClick={() => copyToClipboard(record.name, `name-${idx}`)}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Copy name"
                    >
                      {copied === `name-${idx}` ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>

                {record.priority && (
                  <div className="grid grid-cols-[80px_1fr] gap-4 items-center">
                    <span className="text-slate-500 font-medium">Priority</span>
                    <div className="font-mono text-slate-700 bg-slate-50 px-3 py-1.5 rounded border border-slate-200 w-fit">
                      {record.priority}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-[80px_1fr] gap-4 items-start">
                  <span className="text-slate-500 font-medium mt-2">Value</span>
                  <div className="flex gap-2 w-full min-w-0">
                    <div className="flex-1 font-mono text-slate-700 bg-slate-50 px-3 py-2 rounded border border-slate-200 break-all text-xs leading-relaxed">
                      {record.value}
                    </div>
                    <button
                      onClick={() => copyToClipboard(record.value, `val-${idx}`)}
                      className="self-start p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                      title="Copy value"
                    >
                      {copied === `val-${idx}` ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

